/**
 * recordPayRun — Pay Run Writer (PR2)
 * ────────────────────────────────────
 * Sole sanctioned mutation surface for releasing a pay run. Produces:
 *   1. An append-only PayRunRecord (audit trail)
 *   2. A set of ExpenseLedgerEntry intents, one row per economic line:
 *        • base (labor | subcontractor_payout)
 *        • each approved field-add (field_add)
 *        • each callback deduction (callback_labor | warranty_remediation)
 *
 * HARD RULES (mirror DB trigger guards + canonical pricing policy):
 *   • Resolver gate: getJobPayRun(input).releasable MUST be true.
 *     Most importantly: refuses when fieldAddTotal > fieldAddCap AND any
 *     above-cap add is still pending — reason key
 *     `field_add_requires_accounting_approval`.
 *   • Idempotent on (jobId, partyId, wrapRecordId). Duplicate calls return
 *     the prior record with `idempotent: true` — never double-write.
 *   • NEVER touches Job.financials.contractValue or Job.payoutAmount.
 *   • NEVER emits a `sales_commission` row. That category is reserved for
 *     the commission engine (DB trigger enforces source=commission_engine).
 *   • Append-only. No update / delete paths exist.
 *
 * This module is in-memory for the beta prototype. The shape of the produced
 * ExpenseLedgerEntry intents is identical to the payload the
 * `expense-ledger-write` edge function accepts, so the same records can be
 * forwarded to Supabase later without translation.
 */

import {
  getJobPayRun,
  type GetJobPayRunInput,
  type PayRunView,
  type PayRunHoldReason,
} from "./getJobPayRun";
import type { ExpenseLedgerEntry } from "@/data/jobCost/expenseLedgerBridge";

// ── Record shape (append-only) ─────────────────────────────────────────────

export interface PayRunLedgerLine {
  category: ExpenseLedgerEntry["category"];
  amountCents: number;
  memo: string;
  /** Stable reference back to the source line (FA id, CB id, or "base"). */
  referenceId: string;
}

export interface PayRunRecord {
  id: string;
  jobId: string;
  partyId?: string;
  partyKind: PayRunView["partyKind"];
  wrapRecordId?: string;
  /** Snapshot of the resolver view at release time. */
  view: PayRunView;
  /** Ledger lines produced. */
  ledgerLines: PayRunLedgerLine[];
  /** Sum of ledger lines, dollars. Equals view.netReleasable. */
  netReleased: number;
  releasedBy: string;
  releasedAt: string;
}

export interface RecordPayRunInput extends GetJobPayRunInput {
  /** Acting user id / name. Audit only — no role validation here. */
  releasedBy: string;
  /** Stable idempotency anchor. Defaults to wrapRecord.id when present. */
  wrapRecordId?: string;
  /** Existing records — caller passes the current append-only list. */
  existing: PayRunRecord[];
}

export type RecordPayRunResult =
  | { ok: true; record: PayRunRecord; idempotent: boolean }
  | { ok: false; reason: PayRunHoldReason | "not_releasable" | "missing_party"; view: PayRunView };

// ── Helpers ────────────────────────────────────────────────────────────────

function dollarsToCents(d: number): number {
  return Math.round(d * 100);
}

function buildLedgerLines(view: PayRunView): PayRunLedgerLine[] {
  const lines: PayRunLedgerLine[] = [];
  if (view.baseAmount > 0) {
    lines.push({
      category: view.ledgerCategory,
      amountCents: dollarsToCents(view.baseAmount),
      memo:
        view.partyKind === "internal_crew"
          ? "Internal labor (hybrid model)"
          : "Contractor base payout",
      referenceId: "base",
    });
  }
  for (const add of view.fieldAdds) {
    if (add.status !== "approved") continue;
    lines.push({
      category: "field_add",
      amountCents: dollarsToCents(add.amount),
      memo: add.reason ? `Field add — ${add.reason}` : "Field add",
      referenceId: add.id,
    });
  }
  for (const ded of view.callbackDeductions) {
    if (ded.amount <= 0) continue;
    lines.push({
      category: ded.ledgerCategory,
      amountCents: dollarsToCents(ded.amount),
      memo: `Callback deduction (${ded.severity})`,
      referenceId: ded.callbackId,
    });
  }
  return lines;
}

function findExisting(
  existing: PayRunRecord[],
  jobId: string,
  partyId: string | undefined,
  wrapRecordId: string | undefined,
): PayRunRecord | undefined {
  return existing.find(
    (r) =>
      r.jobId === jobId &&
      r.partyId === partyId &&
      r.wrapRecordId === wrapRecordId,
  );
}

// ── Writer ─────────────────────────────────────────────────────────────────

export function recordPayRun(input: RecordPayRunInput): RecordPayRunResult {
  const view = getJobPayRun(input);
  const wrapRecordId = input.wrapRecordId ?? input.wrapRecord?.id;

  // Idempotency check FIRST — duplicate releases never re-emit ledger rows.
  const prior = findExisting(input.existing, view.jobId, view.partyId, wrapRecordId);
  if (prior) return { ok: true, record: prior, idempotent: true };

  if (!view.releasable) {
    return {
      ok: false,
      reason: view.holdReasons[0] ?? "not_releasable",
      view,
    };
  }
  if (!view.partyId && view.partyKind !== "internal_crew") {
    return { ok: false, reason: "missing_party", view };
  }

  const ledgerLines = buildLedgerLines(view);
  const record: PayRunRecord = {
    id: `PR-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    jobId: view.jobId,
    partyId: view.partyId,
    partyKind: view.partyKind,
    wrapRecordId,
    view,
    ledgerLines,
    netReleased: view.netReleasable,
    releasedBy: input.releasedBy,
    releasedAt: new Date().toISOString(),
  };
  return { ok: true, record, idempotent: false };
}

/**
 * Convert a PayRunRecord's ledger lines into the exact payload shape the
 * `expense-ledger-write` edge function accepts. Caller adds organizationId.
 */
export function payRunLedgerPayloads(
  record: PayRunRecord,
): Array<Omit<ExpenseLedgerEntry, "id" | "createdAt"> & { occurredOn: string }> {
  const occurredOn = record.releasedAt.slice(0, 10);
  return record.ledgerLines.map((line) => ({
    jobId: record.jobId,
    category: line.category,
    source: "manual_entry",
    amountCents: line.amountCents,
    occurredOn,
    memo: line.memo,
    referenceId: line.referenceId,
  }));
}
